import React, { useMemo, ReactElement } from 'react';
import Table from '../../../components/table/table.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import FormFieldNumber from '../../../components/form-field-number/form-field-number.component';
import FormFieldDate from '../../../components/form-field-date/form-field-date.component';
import SinglePageTab from '../../app/common/single-page-tab/single-page-tab.component';
import FormatService from '../../../services/format-service';

const AssetPropertyCapitalization: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isSaving,
    isSaveDisabled,
    isEditMode,
    isRightDrawerOpen,
    assetData,
    formError,
    onCancelForm,
    onSubmitForm,
  } = props;

  const formPrefix: string = 'assetData';
  const parsedMostRecentAppraisalDate: Date = assetData && assetData.mostRecentAppraisalDate
    && new Date(assetData.mostRecentAppraisalDate);
  const isEmpty: boolean = !isEditMode && !assetData.amount && !assetData.yardiAppraisedValue
    && !assetData.mostRecentAppraisalDate;

  const mostRecentAppraisalDate: string = useMemo((): string => (
    parsedMostRecentAppraisalDate && FormatService.formatDateString(parsedMostRecentAppraisalDate)
  ), [parsedMostRecentAppraisalDate]);

  return (
    <SinglePageTab
      isSaving={isSaving}
      isEmpty={isEmpty}
      isSaveDisabled={isSaveDisabled}
      isEditMode={isEditMode}
      isRightDrawerOpen={isRightDrawerOpen}
      formError={formError}
      onCancelForm={onCancelForm}
      onSubmitForm={onSubmitForm}
    >
      {
        (!isEmpty || isEditMode) && (
          <Table>
            <TableBody isPlain withNoBorders withTwoColumns withHeaderColumn>
              {
                (isEditMode || assetData.amount) && (
                  <TableRow>
                    <TableCell>
                      Total Capitalization
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isBordered name={`${formPrefix}.amount`} />
                        ) : assetData.amount
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || assetData.value) && (
                  <TableRow>
                    <TableCell>
                      Total Value
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldNumber isBordered name={`${formPrefix}.value`} />
                        ) : assetData.value
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                (isEditMode || mostRecentAppraisalDate) && (
                  <TableRow>
                    <TableCell>
                      Most Recent Appraisal Date
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FormFieldDate
                            isBordered
                            withBlankValues
                            name={`${formPrefix}.mostRecentAppraisalDate`}
                          />
                        ) : mostRecentAppraisalDate
                      }
                    </TableCell>
                  </TableRow>
                )
              }
              {
                !isEditMode && assetData.lastDeal && assetData.lastDeal.capitalizationSummaryData
                && assetData.lastDeal.capitalizationSummaryData.sources
                && assetData.lastDeal.capitalizationSummaryData.sources.debt
                && assetData.lastDeal.capitalizationSummaryData.sources.debt.amount && (
                  <TableRow>
                    <TableCell>
                      Total Debt
                    </TableCell>
                    <TableCell>
                      {assetData.lastDeal.capitalizationSummaryData.sources.debt.amount}
                    </TableCell>
                  </TableRow>
                )
              }
              {
                !isEditMode && assetData.lastDeal && assetData.lastDeal.capitalizationSummaryData
                && assetData.lastDeal.capitalizationSummaryData.sources
                && assetData.lastDeal.capitalizationSummaryData.sources.equity
                && assetData.lastDeal.capitalizationSummaryData.sources.equity.amount && (
                  <TableRow>
                    <TableCell>
                      Total Equity
                    </TableCell>
                    <TableCell>
                      {assetData.lastDeal.capitalizationSummaryData.sources.equity.amount}
                    </TableCell>
                  </TableRow>
                )
              }
              {
                !isEditMode && assetData.lastDeal && assetData.lastDeal.capitalizationSummaryData
                && assetData.lastDeal.capitalizationSummaryData.sources
                && assetData.lastDeal.capitalizationSummaryData.sources.generalPartner
                && assetData.lastDeal.capitalizationSummaryData.sources.generalPartner.amount && (
                  <TableRow>
                    <TableCell>
                      Total GP Equity
                    </TableCell>
                    <TableCell>
                      {assetData.lastDeal.capitalizationSummaryData.sources.generalPartner.amount}
                    </TableCell>
                  </TableRow>
                )
              }
              {
                !isEditMode && assetData.yardiAppraisedValue && (
                  <TableRow>
                    <TableCell>
                      Appraised Value (Yardi)
                    </TableCell>
                    <TableCell>
                      {assetData.yardiAppraisedValue}
                    </TableCell>
                  </TableRow>
                )
              }
            </TableBody>
          </Table>
        )
      }
    </SinglePageTab>
  );
});

export default AssetPropertyCapitalization;
